import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getApiErrorUserMessage, readApiError } from "_shared/_errors/api-error";
import { getApiErrorData, getApiErrorStatus, isFetchBaseQueryError } from "_shared/_errors/api-error-message";
import { api } from "_shared/_stores/api";
import EmailAddress from "_shared/email-addresses/email-address";
import { InvalidInvitationError } from "_shared/entity-members/_api/entity-member-api";
import Guid from "_shared/guids/guid";
import Name from "_shared/names/name";
import { MemberInvitationNotOpenError, UserMemberInvitationNotFoundError } from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import AcceptOpenEntityMemberInvitationAPIRequest from "legal-entities/invitations/member-invitations/open/_api/commands/accept-open-entity-member-invitation-api-command";
import { OpenEntityMemberInvitationJSON } from "legal-entities/invitations/member-invitations/open/_schema/open-entity-member-invitation-schema";
import OpenEntityMemberInvitation from "legal-entities/invitations/member-invitations/open/open-entity-member-invitation";

type CreateOpenMemberInvitationArgs = {
  entityId: Guid;
};

type GetOpenMemberInvitationsArgs = {
  entityId: Guid;
};

type OpenMemberInvitationIdArgs = {
  id: Guid;
};

type AcceptOpenMemberInvitationArgs = {
  invitation: OpenEntityMemberInvitation;
  email: EmailAddress;
  name: Name;
};

type DeleteOpenMemberInvitationArgs = {
  invitation: OpenEntityMemberInvitation;
};

const jsonHeaders = {
  "Content-Type": "application/json",
};

export const openEntityMemberInvitationApi = api.injectEndpoints({
  endpoints: (build) => ({
    createOpenMemberInvitation: build.mutation<OpenEntityMemberInvitation, CreateOpenMemberInvitationArgs>({
      async queryFn({ entityId }, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery({
          url: `legal-entities/${entityId.value}/open-member-invitations`,
          method: "POST",
          body: JSON.stringify({}),
          headers: jsonHeaders,
        });

        if (response.error) {
          if (getApiErrorStatus(response.error) === 400) {
            return { error: toQueryError(new InvalidInvitationError(String(getApiErrorData(response.error)))) };
          }
          return {
            error: toQueryError(new OpenEntityMemberInvitationAPIServiceError("createOpenMemberInvitation", response.error)),
          };
        }

        return { data: OpenEntityMemberInvitation.fromJSON(response.data as OpenEntityMemberInvitationJSON) };
      },
    }),
    getAllOpenEntityMemberInvitations: build.query<OpenEntityMemberInvitation[], GetOpenMemberInvitationsArgs>({
      async queryFn({ entityId }, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery(`legal-entities/${entityId.value}/open-member-invitations`);

        if (response.error) {
          return {
            error: toQueryError(
              new OpenEntityMemberInvitationAPIServiceError("getAllOpenEntityMemberInvitationAsync", response.error),
            ),
          };
        }

        return { data: OpenEntityMemberInvitation.fromJSONArray(response.data as OpenEntityMemberInvitationJSON[]) ?? [] };
      },
    }),
    getOpenMemberInvitationById: build.query<OpenEntityMemberInvitation, OpenMemberInvitationIdArgs>({
      async queryFn({ id }, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery(`legal-entities/open-member-invitations/${id.value}`);

        if (response.error) {
          if (getApiErrorStatus(response.error) === 404) {
            return { error: toQueryError(new UserMemberInvitationNotFoundError(id)) };
          }

          return {
            error: toQueryError(new OpenEntityMemberInvitationAPIServiceError("getOpenMemberInvitationById", response.error)),
          };
        }

        return { data: OpenEntityMemberInvitation.fromJSON(response.data as OpenEntityMemberInvitationJSON) };
      },
    }),
    acceptOpenEntityMemberInvitation: build.mutation<void, AcceptOpenMemberInvitationArgs>({
      async queryFn({ invitation, email, name }, _queryApi, _extraOptions, baseQuery) {
        if (!invitation.id) {
          return { error: toQueryError(new Error("Invitation ID is required to accept an invitation.")) };
        }

        const response = await baseQuery({
          url: `legal-entities/open-member-invitations/${invitation.id.value}/accept`,
          method: "POST",
          body: JSON.stringify(new AcceptOpenEntityMemberInvitationAPIRequest(email, name)),
          headers: jsonHeaders,
        });

        if (response.error) {
          if (getApiErrorStatus(response.error) === 404) {
            return { error: toQueryError(new UserMemberInvitationNotFoundError(invitation.id)) };
          }
          if (getApiErrorStatus(response.error) === 409) {
            return { error: toQueryError(new MemberInvitationNotOpenError(conflictMessage(response.error))) };
          }
          if (getApiErrorStatus(response.error) === 400) {
            return { error: toQueryError(new InvalidInvitationError(String(getApiErrorData(response.error)))) };
          }

          return {
            error: toQueryError(
              new OpenEntityMemberInvitationAPIServiceError(
                "acceptOpenEntityMemberInvitationAsync",
                response.error,
                invitation,
              ),
            ),
          };
        }

        return { data: null as unknown as void };
      },
    }),
    deleteOpenMemberInvitation: build.mutation<void, DeleteOpenMemberInvitationArgs>({
      async queryFn({ invitation }, _queryApi, _extraOptions, baseQuery) {
        if (!invitation.id) {
          return { error: toQueryError(new Error("Invitation ID is required to delete an invitation.")) };
        }

        const response = await baseQuery({
          url: `legal-entities/${invitation.entity.id.value}/open-member-invitations/${invitation.id.value}`,
          method: "DELETE",
        });

        if (response.error) {
          if (getApiErrorStatus(response.error) === 404) {
            return { error: toQueryError(new UserMemberInvitationNotFoundError(invitation.id)) };
          }

          return {
            error: toQueryError(
              new OpenEntityMemberInvitationAPIServiceError("deleteOpenMemberInvitation", response.error, invitation),
            ),
          };
        }

        return { data: null as unknown as void };
      },
    }),
  }),
});

export { InvalidInvitationError };

export class OpenEntityMemberInvitationAPIServiceError extends Error {
  invitation?: OpenEntityMemberInvitation;
  method: string;
  error: unknown;

  constructor(method: string, error: unknown, invitation?: OpenEntityMemberInvitation) {
    super(`OpenEntityMemberInvitationAPIService: ${error instanceof Error ? error.message : String(error)}`);
    this.method = method;
    this.error = error;
    this.invitation = invitation;
  }
}

export function getOpenEntityMemberInvitationAPIErrorData(error: unknown): unknown {
  if (!(error instanceof OpenEntityMemberInvitationAPIServiceError)) return undefined;
  if (!isFetchBaseQueryError(error.error)) return undefined;
  return getApiErrorData(error.error);
}

function toQueryError(error: Error): FetchBaseQueryError {
  return error as unknown as FetchBaseQueryError;
}

function conflictMessage(error: unknown): string | undefined {
  const result = readApiError(error);
  return result.trusted ? getApiErrorUserMessage(result) : undefined;
}

export const {
  useCreateOpenMemberInvitationMutation,
  useGetAllOpenEntityMemberInvitationsQuery,
  useLazyGetAllOpenEntityMemberInvitationsQuery,
  useLazyGetOpenMemberInvitationByIdQuery,
  useGetOpenMemberInvitationByIdQuery,
  useAcceptOpenEntityMemberInvitationMutation,
  useDeleteOpenMemberInvitationMutation,
} = openEntityMemberInvitationApi;
